/**
 * @fileoverview Question component that displays a question and its options.
 * It also handles the transition between questions and displays a prompt modal when expired.
 *
 * @example
 * // Basic usage
 * import { Question } from './Question'
 *
 * function TestQuestion() {
 *   return <Question assignmentId="123" question={...} isLegacyBattery={false} />
 * }
 */

import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import config from '../config'

import { AudioQuestionFactory } from './questions/AudioQuestionFactory'
import { CheckboxQuestionFactory } from './questions/CheckboxQuestionFactory'
import { ChoiceQuestionFactory } from './questions/ChoiceQuestionFactory'
import { PromptModalContent } from './questions/PromptModalContent'
import { Form } from './questions/forms'

import { MiddlewareContext } from '../MiddlewareContext'
import { ModalContext } from '../ModalContext'
import { useSessionStorage } from '../hooks/useSessionStorage'

import css from './Question.module.css'
import { Redirect } from './Redirect'

/**
 * Mirror for global.d.ts QuestionType enum
 * Since d.ts files are for the compile time only,
 * we can't refer to its values through the Novoic.* interface
 *
 * In order to use these enums as values, we need to create
 * a mirror, runtime-friendly enum like this one.
 */
enum QuestionType {
    AUDIO = 'audio',
    FORM = 'jsonform',
    CHECKBOX = 'checkbox',
    CHOICE = 'choice',
    REDIRECT = 'redirect',
}

/**
 * @function formatLanguage
 * @description Formats a language code to the format used by i18next.
 * @param {string} language - The language code to format.
 * @returns {string} The formatted language code.
 */
function formatLanguage(language = 'en-US'): string {
    return language.replace('-', '_')
}

/**
 * @function createQuestionContent
 * @description Creates the content for a question based on its type.
 * @param {Novoic.AnyQuestion} question - The question object.
 * @param {Record<string, unknown>} errors - The errors object.
 * @param {Object} options - The options object.
 * @returns {JSX.Element} The rendered question content.
 */
function createQuestionContent(
    question: Novoic.AnyQuestion,
    errors: Record<string, unknown>,
    {
        isLegacyBattery,
        submit,
        exit,
    }: {
        isLegacyBattery: boolean
        submit: (questionID: string, answer: unknown) => Promise<void | Record<string, unknown>>
        exit: () => Promise<void>
    }
): JSX.Element {
    let content: JSX.Element

    switch (question?.type) {
        case QuestionType.AUDIO:
            if (isLegacyBattery) {
                content = (
                    <AudioQuestionFactory
                        errors={errors}
                        question={question as Novoic.AudioQuestion}
                        onSubmit={submit}
                        onExit={exit}
                    />
                )
            } else {
                content = (
                    <Form
                        errors={errors}
                        question={question as Novoic.FormQuestion}
                        onSubmit={submit}
                        onExit={exit}
                    />
                )
            }
            break

        case QuestionType.CHECKBOX:
            content = (
                <CheckboxQuestionFactory
                    errors={errors}
                    question={question as Novoic.CheckboxQuestion}
                    onSubmit={submit}
                    onExit={exit}
                />
            )
            break

        case QuestionType.CHOICE:
            content = (
                <ChoiceQuestionFactory
                    errors={errors}
                    question={question as Novoic.ChoiceQuestion}
                    onSubmit={submit}
                    onExit={exit}
                />
            )
            break

        case QuestionType.FORM:
            content = (
                <Form
                    errors={errors}
                    question={question as Novoic.FormQuestion}
                    onSubmit={submit}
                    onExit={exit}
                />
            )
            break
        case QuestionType.REDIRECT:
            content = (
                <Redirect
                    question={question as Novoic.RedirectQuestion}
                    onSubmit={submit}
                    onExit={exit}
                />
            )
            break
        default:
            content = <div>Expired</div>
    }

    return content
}

/**
 * Routing component that renders a question based on its type
 * @param {QuestionProps} props Props wrapper for question object and submit handler
 * @returns {JSX.Element}
 */
export function Question(): JSX.Element {
    const {
        isExpired,
        isLegacyBattery,
        submit,
        standalone,
        exit,
        errors,
        onTimeout,
        onLanguageChange,
        ...rest
    } = useContext(MiddlewareContext)
    let { question } = rest

    const modal = useContext(ModalContext)
    const { get } = useSessionStorage()
    const { t, i18n } = useTranslation()

    useEffect(() => {
        if (question.language && formatLanguage(question.language) !== i18n.language) {
            const lang = formatLanguage(question.language || config.defaultLanguage)
            i18n.changeLanguage(lang)
            onLanguageChange(lang)
        }
        window.scrollTo(0, 0)
    }, [question?.id])

    useEffect(() => {
        if (isExpired) {
            modal.open({
                render: ({ onCancel, onConfirm }) => (
                    <PromptModalContent
                        text={t('modals.expired.text')}
                        caption={t('modals.expired.caption')}
                        confirmText={t('modals.expired.buttons.confirm')}
                        cancelText={t('modals.expired.buttons.cancel')}
                        onCancel={async () => {
                            await exit()
                            onCancel()
                        }}
                        onConfirm={async () => {
                            await onTimeout()
                            onConfirm()
                        }}
                    />
                ),
                onClose: exit,
            })
        }
    }, [isExpired])

    function renderQuestionContent(
        question: Novoic.AnyQuestion,
        errors: Record<string, unknown>,
        {
            isLegacyBattery,
            standalone,
            submit,
            exit,
        }: {
            isLegacyBattery: boolean
            standalone: boolean
            submit: (questionID: string, answer: unknown) => Promise<void | Record<string, unknown>>
            exit: () => Promise<void>
        }
    ) {
        const content = createQuestionContent(question, errors, {
            isLegacyBattery,
            submit,
            exit,
        })

        if (question?.type === QuestionType.REDIRECT) {
            return <div>{content}</div>
        }

        return (
            <>
                <div className={css.container}>
                    <div className={css.content}>{content}</div>
                    {!standalone && (
                        <img
                            width={207}
                            height={21}
                            className={css.logo}
                            src={config.s3.getStaticURL('logo.svg')}
                            alt="Novoic Storyteller"
                        />
                    )}
                </div>
            </>
        )
    }

    if (!question) {
        question = get('lastQuestion')
        if (!question) {
            return null
        }
    }

    if (question.language && formatLanguage(question.language) !== i18n.language) {
        return null
    }

    return (
        <SwitchTransition>
            <CSSTransition
                key={question?.id}
                timeout={300}
                mountOnEnter
                unmountOnExit
                classNames={{
                    enter: css.transition_enter,
                    enterActive: css.transition_enter_active,
                    exit: css.transition_exit,
                    exitActive: css.transition_exit_active,
                }}
            >
                {renderQuestionContent(question, errors, {
                    isLegacyBattery,
                    standalone,
                    submit,
                    exit,
                })}
            </CSSTransition>
        </SwitchTransition>
    )
}
